import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import apiClient from '../../../../../utils/apiClient';
import handleEmptyTableResponse from '../../../common/table/utils/handle-empty-table-response';
import { isPmaManagerRole } from '../../../../../utils/role-utils';
import {
  getNextSortDirection,
  getStoredFilters,
  saveFilters,
  sortRows,
} from '../../../../../utils/tableUtils';
import {
  DEFAULT_FILTERS,
  EMPTY_FILTERS,
  FILTERS_STORAGE_KEY,
} from '../domain/table-hac.constants';
import {
  SORT_COMPARATORS,
  filterHacs,
  getRetailerOptions,
  sortHacsForPma,
} from '../domain/table-hac.utils';

function usePmaHacTable({ reload, onDataLoaded, t }) {
  const token = sessionStorage.getItem('token') || null;
  const decodedToken = jwtDecode(token);
  const pmaId = decodedToken.id;
  const isPmaManager = isPmaManagerRole(decodedToken.role);

  const [dataLoaded, setDataLoaded] = useState(false);
  const [pmas, setPmas] = useState([]);
  const [hacs, setHacs] = useState([]);
  const [filteredHacs, setFilteredHacs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [filters, setFilters] = useState(() => {
    const storedFilters = getStoredFilters(FILTERS_STORAGE_KEY, DEFAULT_FILTERS);
    return isPmaManager ? storedFilters : { ...storedFilters, pma: '' };
  });

  const fetchData = async () => {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
    };

    try {
      const response = await apiClient.get(`/pma/hacs?pmaId=${pmaId}`, config);

      if (response.status === 200) {
        const sortedHacs = sortHacsForPma(response.data);

        setHacs(sortedHacs);
        setFilteredHacs(filterHacs(sortedHacs, filters));
        setDataLoaded(true);
        if (typeof onDataLoaded === 'function') onDataLoaded();
      }

      if (response.status === 204) {
        handleEmptyTableResponse({
          clearStateSetters: [setHacs, setFilteredHacs],
          onDataLoaded,
          setDataLoaded,
        });
      }
    } catch (error) {
      if (error.response) {
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'));
      }
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!reload) return;
    fetchData();
    // eslint-disable-next-line
  }, [reload]);

  useEffect(() => {
    if (!isPmaManager) {
      setPmas([]);
      return undefined;
    }

    let active = true;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
    };

    const fetchPmas = async () => {
      try {
        const response = await apiClient.get(`/pma/pmaGm/pmas?pmaId=${pmaId}`, config);
        if (active && response.status === 200) {
          setPmas(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (active) setPmas([]);
      }
    };

    fetchPmas();

    return () => {
      active = false;
    };
  }, [isPmaManager, pmaId, token]);

  useEffect(() => {
    setCountries([...new Set(hacs.map((hac) => hac.area_code))]);
  }, [hacs]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = {
      ...filters,
      [name]: value,
    };

    if (name === 'retailer' && value) {
      const matched = hacs.find((hac) => hac.retailer_name === value);
      if (matched) {
        updatedFilters.country = matched.area_code;
      }
    }

    setFilters(updatedFilters);
  };

  const applyFilters = (appliedFilters = filters, hacsData = hacs) => {
    setFilteredHacs(filterHacs(hacsData, appliedFilters));
    saveFilters(FILTERS_STORAGE_KEY, appliedFilters);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    applyFilters(EMPTY_FILTERS, hacs);
  };

  const handleRequestSort = (property) => {
    setOrder(getNextSortDirection(orderBy, order, property));
    setOrderBy(property);
  };

  return {
    countries,
    dataLoaded,
    filters,
    filteredHacs,
    hacs,
    isPmaManager,
    order,
    orderBy,
    pmas,
    retailers: getRetailerOptions(hacs, filters),
    rows: sortRows(filteredHacs, orderBy, order, SORT_COMPARATORS),
    snackbarMessage,
    snackbarOpen,
    handleApplyFilter: () => applyFilters(),
    handleFilterChange,
    handleRequestSort,
    handleSnackbarClose: () => setSnackbarOpen(false),
    resetFilters,
  };
}

export default usePmaHacTable;
